import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Bell, Plus, Pencil, Trash2, AlertTriangle, Zap, WifiOff } from 'lucide-react';
import { toast } from 'sonner';

const ALERT_TYPES = [
    { value: 'power_threshold', label: 'Limite de Potência', icon: Zap },
    { value: 'energy_threshold', label: 'Limite de Energia', icon: AlertTriangle },
    { value: 'offline', label: 'Dispositivo Offline', icon: WifiOff }
];

export default function Alerts() {
    const { api } = useAuth();
    const [alerts, setAlerts] = useState([]);
    const [devices, setDevices] = useState([]);
    const [areas, setAreas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingAlert, setEditingAlert] = useState(null);
    
    const [formData, setFormData] = useState({
        name: '',
        device_id: '',
        area_id: '',
        alert_type: 'power_threshold',
        threshold_value: '',
        comparison: 'above',
        enabled: true
    });

    const fetchData = async () => {
        try {
            const [alertsRes, devicesRes, areasRes] = await Promise.all([
                api.get('/alerts'),
                api.get('/devices'),
                api.get('/areas')
            ]);
            setAlerts(alertsRes.data);
            setDevices(devicesRes.data);
            setAreas(areasRes.data);
        } catch (error) {
            console.error('Error fetching alerts:', error);
            toast.error('Erro ao carregar alertas');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const resetForm = () => {
        setFormData({
            name: '',
            device_id: '',
            area_id: '',
            alert_type: 'power_threshold',
            threshold_value: '',
            comparison: 'above',
            enabled: true
        });
        setEditingAlert(null);
    };

    const handleOpenDialog = (alert = null) => {
        if (alert) {
            setEditingAlert(alert);
            setFormData({
                name: alert.name,
                device_id: alert.device_id || '',
                area_id: alert.area_id || '',
                alert_type: alert.alert_type,
                threshold_value: alert.threshold_value?.toString() || '',
                comparison: alert.comparison,
                enabled: alert.enabled
            });
        } else {
            resetForm();
        }
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        resetForm();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            const payload = {
                name: formData.name,
                device_id: formData.device_id || null,
                area_id: formData.area_id || null,
                alert_type: formData.alert_type,
                threshold_value: formData.threshold_value ? parseFloat(formData.threshold_value) : null,
                comparison: formData.comparison,
                enabled: formData.enabled
            };

            if (editingAlert) {
                await api.put(`/alerts/${editingAlert.id}`, payload);
                toast.success('Alerta atualizado com sucesso');
            } else {
                await api.post('/alerts', payload);
                toast.success('Alerta criado com sucesso');
            }
            
            handleCloseDialog();
            fetchData();
        } catch (error) {
            console.error('Error saving alert:', error);
            toast.error(error.response?.data?.detail || 'Erro ao guardar alerta');
        }
    };

    const handleDelete = async (alertId) => {
        try {
            await api.delete(`/alerts/${alertId}`);
            toast.success('Alerta eliminado com sucesso');
            fetchData();
        } catch (error) {
            console.error('Error deleting alert:', error);
            toast.error('Erro ao eliminar alerta');
        }
    };

    const handleToggleEnabled = async (alert) => {
        try {
            await api.put(`/alerts/${alert.id}`, {
                ...alert,
                enabled: !alert.enabled
            });
            toast.success(`Alerta ${!alert.enabled ? 'ativado' : 'desativado'}`);
            fetchData();
        } catch (error) {
            console.error('Error toggling alert:', error);
            toast.error('Erro ao alterar estado do alerta');
        }
    };

    const getDeviceName = (deviceId) => {
        const device = devices.find(d => d.id === deviceId);
        return device?.name || 'Todos';
    };

    const getAreaName = (areaId) => {
        const area = areas.find(a => a.id === areaId);
        return area?.name || 'Todas';
    };

    const getAlertTypeInfo = (type) => {
        return ALERT_TYPES.find(t => t.value === type) || ALERT_TYPES[0];
    };

    const formatThreshold = (alert) => {
        if (alert.alert_type === 'offline') return 'N/A';
        const comparison = alert.comparison === 'above' ? '>' : '<';
        const unit = alert.alert_type === 'power_threshold' ? 'W' : 'kWh';
        return `${comparison} ${alert.threshold_value} ${unit}`;
    };

    if (loading) {
        return (
            <div className="space-y-6" data-testid="alerts-loading">
                <div className="flex justify-between items-center">
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="h-10 w-36" />
                </div>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {[...Array(6)].map((_, i) => (
                        <Skeleton key={i} className="h-48 rounded-lg" />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="alerts-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold font-heading text-slate-900">Alertas</h1>
                    <p className="text-slate-500">Configure alertas para monitorização de limites</p>
                </div>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogTrigger asChild>
                        <Button
                            className="bg-emerald-600 hover:bg-emerald-700"
                            onClick={() => handleOpenDialog()}
                            data-testid="add-alert-btn"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Criar Alerta
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                            <DialogTitle className="font-heading">
                                {editingAlert ? 'Editar Alerta' : 'Criar Alerta'}
                            </DialogTitle>
                            <DialogDescription>
                                {editingAlert ? 'Atualize a configuração do alerta' : 'Configure um novo alerta de monitorização'}
                            </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="name">Nome do Alerta</Label>
                                <Input
                                    id="name"
                                    placeholder="Ex: Consumo Elevado Fábrica"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    required
                                    data-testid="alert-name-input"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label>Tipo de Alerta</Label>
                                <Select
                                    value={formData.alert_type}
                                    onValueChange={(value) => setFormData({ ...formData, alert_type: value })}
                                >
                                    <SelectTrigger data-testid="alert-type-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {ALERT_TYPES.map((type) => (
                                            <SelectItem key={type.value} value={type.value}>
                                                {type.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label>Dispositivo (opcional)</Label>
                                <Select
                                    value={formData.device_id || "all"}
                                    onValueChange={(value) => setFormData({ ...formData, device_id: value === "all" ? "" : value })}
                                >
                                    <SelectTrigger data-testid="alert-device-select">
                                        <SelectValue placeholder="Todos os dispositivos" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Todos</SelectItem>
                                        {devices.map((device) => (
                                            <SelectItem key={device.id} value={device.id}>
                                                {device.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label>Área (opcional)</Label>
                                <Select
                                    value={formData.area_id || "all"}
                                    onValueChange={(value) => setFormData({ ...formData, area_id: value === "all" ? "" : value })}
                                >
                                    <SelectTrigger data-testid="alert-area-select">
                                        <SelectValue placeholder="Todas as áreas" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Todas</SelectItem>
                                        {areas.map((area) => (
                                            <SelectItem key={area.id} value={area.id}>
                                                {area.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {formData.alert_type !== 'offline' && (
                                <>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label>Comparação</Label>
                                            <Select
                                                value={formData.comparison}
                                                onValueChange={(value) => setFormData({ ...formData, comparison: value })}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="above">Acima de</SelectItem>
                                                    <SelectItem value="below">Abaixo de</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="threshold">Valor Limite</Label>
                                            <Input
                                                id="threshold"
                                                type="number"
                                                placeholder={formData.alert_type === 'power_threshold' ? 'Watts' : 'kWh'}
                                                value={formData.threshold_value}
                                                onChange={(e) => setFormData({ ...formData, threshold_value: e.target.value })}
                                                required
                                                data-testid="alert-threshold-input"
                                            />
                                        </div>
                                    </div>
                                </>
                            )}

                            <div className="flex items-center justify-between">
                                <Label htmlFor="enabled">Alerta Ativo</Label>
                                <Switch
                                    id="enabled"
                                    checked={formData.enabled}
                                    onCheckedChange={(checked) => setFormData({ ...formData, enabled: checked })}
                                />
                            </div>

                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={handleCloseDialog}>
                                    Cancelar
                                </Button>
                                <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="alert-save-btn">
                                    {editingAlert ? 'Guardar' : 'Criar'}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Alerts Grid */}
            {alerts.length === 0 ? (
                <Card className="p-12" data-testid="no-alerts">
                    <div className="text-center">
                        <Bell className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                        <h3 className="text-lg font-semibold text-slate-900 mb-2">Nenhum alerta configurado</h3>
                        <p className="text-slate-500 mb-6 max-w-md mx-auto">
                            Crie alertas para ser notificado quando o consumo ou produção ultrapassar determinados limites.
                        </p>
                        <Button
                            className="bg-emerald-600 hover:bg-emerald-700"
                            onClick={() => handleOpenDialog()}
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Criar Primeiro Alerta
                        </Button>
                    </div>
                </Card>
            ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {alerts.map((alert) => {
                        const typeInfo = getAlertTypeInfo(alert.alert_type);
                        const TypeIcon = typeInfo.icon;
                        
                        return (
                            <Card key={alert.id} className={`hover:shadow-md transition-shadow ${!alert.enabled ? 'opacity-60' : ''}`} data-testid={`alert-card-${alert.id}`}>
                                <CardHeader className="pb-3">
                                    <div className="flex items-start justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className={`p-2 rounded-lg ${alert.enabled ? 'bg-amber-50' : 'bg-slate-100'}`}>
                                                <TypeIcon className={`w-5 h-5 ${alert.enabled ? 'text-amber-600' : 'text-slate-400'}`} />
                                            </div>
                                            <div>
                                                <CardTitle className="text-base font-heading">{alert.name}</CardTitle>
                                                <CardDescription className="text-xs">
                                                    {typeInfo.label}
                                                </CardDescription>
                                            </div>
                                        </div>
                                        <Switch
                                            checked={alert.enabled}
                                            onCheckedChange={() => handleToggleEnabled(alert)}
                                            data-testid={`toggle-alert-${alert.id}`}
                                        />
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-4">
                                        <div className="space-y-2 text-sm">
                                            <div className="flex justify-between">
                                                <span className="text-slate-500">Dispositivo:</span>
                                                <span className="font-medium">{getDeviceName(alert.device_id)}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-slate-500">Área:</span>
                                                <span className="font-medium">{getAreaName(alert.area_id)}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-slate-500">Condição:</span>
                                                <span className="font-mono font-medium">{formatThreshold(alert)}</span>
                                            </div>
                                        </div>
                                        
                                        <div className="flex items-center gap-2 pt-2 border-t">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleOpenDialog(alert)}
                                                data-testid={`edit-alert-${alert.id}`}
                                            >
                                                <Pencil className="w-4 h-4 mr-1" />
                                                Editar
                                            </Button>
                                            <AlertDialog>
                                                <AlertDialogTrigger asChild>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                        data-testid={`delete-alert-${alert.id}`}
                                                    >
                                                        <Trash2 className="w-4 h-4 mr-1" />
                                                        Eliminar
                                                    </Button>
                                                </AlertDialogTrigger>
                                                <AlertDialogContent>
                                                    <AlertDialogHeader>
                                                        <AlertDialogTitle>Eliminar Alerta?</AlertDialogTitle>
                                                        <AlertDialogDescription>
                                                            Esta ação não pode ser revertida. O alerta será permanentemente eliminado.
                                                        </AlertDialogDescription>
                                                    </AlertDialogHeader>
                                                    <AlertDialogFooter>
                                                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                                        <AlertDialogAction
                                                            className="bg-red-600 hover:bg-red-700"
                                                            onClick={() => handleDelete(alert.id)}
                                                        >
                                                            Eliminar
                                                        </AlertDialogAction>
                                                    </AlertDialogFooter>
                                                </AlertDialogContent>
                                            </AlertDialog>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
